import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Plus, Trash2, Save, Eye, FileText, 
  Calculator, AlertCircle, CheckCircle, Loader2, X, Users,
  Building2, Briefcase, User, HandCoins, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de beneficiario
const TIPOS_BENEFICIARIO = [
  { value: 'aliado_referido', label: 'Aliado referidor', icon: Users, description: 'Aliado que refiere clientes y recibe comisiones' },
  { value: 'proveedor', label: 'Proveedor', icon: Building2, description: 'Proveedor de productos o servicios' },
  { value: 'tercero', label: 'Tercero', icon: User, description: 'Tercero sin relación comercial directa' },
  { value: 'prestador_servicio', label: 'Prestador de servicio', icon: Briefcase, description: 'Persona que presta servicios profesionales' },
  { value: 'otro', label: 'Otro', icon: HandCoins, description: 'Otro tipo de beneficiario' }
];

// Tipos de concepto (no solo referidos)
const TIPOS_CONCEPTO = [
  { value: 'producto', label: 'Producto', requiereCalculo: false },
  { value: 'servicio', label: 'Servicio', requiereCalculo: false },
  { value: 'comision_referido', label: 'Comisión por referido', requiereCalculo: true },
  { value: 'honorarios', label: 'Honorarios', requiereCalculo: false },
  { value: 'gestion', label: 'Gestión', requiereCalculo: false },
  { value: 'bonificacion', label: 'Bonificación', requiereCalculo: false },
  { value: 'pago_especial', label: 'Pago especial', requiereCalculo: false },
  { value: 'otro', label: 'Otro concepto', requiereCalculo: false }
];

// Tipos de referido (solo cuando el tipo es comision_referido)
const TIPOS_REFERIDO = [
  { value: 'novio', label: 'Novio', porcentaje: 5 },
  { value: 'invitado', label: 'Invitado', porcentaje: 3 },
  { value: 'mascota', label: 'Mascota', porcentaje: 3 },
  { value: 'paje', label: 'Paje', porcentaje: 3 },
  { value: 'corte_completa', label: 'Corte Completa', porcentaje: 5 },
  { value: 'otro', label: 'Otro', porcentaje: 2 }
];

const BANCOS = [
  'Bancolombia', 'Davivienda', 'BBVA', 'Banco de Bogotá', 
  'Banco de Occidente', 'Banco Popular', 'Scotiabank Colpatria',
  'Banco AV Villas', 'Banco Caja Social', 'Nequi', 'Daviplata', 'Otro'
];

const CrearCuentaCobroManual = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  
  // Tipo de beneficiario (NUEVO)
  const [tipoBeneficiario, setTipoBeneficiario] = useState('');
  
  // Modo de creación - ahora derivado del tipo de beneficiario
  const modoCreacion = tipoBeneficiario === 'aliado_referido' ? 'referidos' : 'manual';
  const esFlujoDirec = tipoBeneficiario && tipoBeneficiario !== 'aliado_referido';
  
  // Aliados
  const [aliados, setAliados] = useState([]);
  const [aliadoSeleccionado, setAliadoSeleccionado] = useState('');
  const [aliadoData, setAliadoData] = useState(null);
  const [usarDatosManual, setUsarDatosManual] = useState(false);
  
  // Referidos disponibles (modo referidos)
  const [referidosDisponibles, setReferidosDisponibles] = useState([]);
  const [referidosSeleccionados, setReferidosSeleccionados] = useState([]);
  
  // Datos manuales del aliado
  const [datosAliado, setDatosAliado] = useState({
    nombre: '',
    tipo_documento: 'cc',
    numero_documento: '',
    telefono: '',
    direccion: '',
    correo: '',
    ciudad: 'Bogotá'
  });
  
  // Datos bancarios
  const [datosBancarios, setDatosBancarios] = useState({
    titular_cuenta: '',
    tipo_documento_titular: 'cc',
    documento_titular: '',
    banco: '',
    tipo_cuenta: 'ahorros',
    numero_cuenta: '',
    llave_pago: ''
  });
  
  // Datos del cobro
  const [datosCobro, setDatosCobro] = useState({
    debe_a: '', // Se llenará automáticamente con el nombre del aliado
    por_concepto: 'Pago de comisiones y conceptos varios del programa de aliados Juan Pablo Murillo Novios',
    periodo: '',
    observaciones: ''
  });
  
  // Conceptos manuales - ahora con tipos de concepto flexibles
  const [conceptosManuales, setConceptosManuales] = useState([{
    id: Date.now(),
    tipo_concepto: 'servicio', // Por defecto servicio (no referido)
    concepto_cobro: '', // Concepto libre
    cliente_tercero: '', // Opcional
    tipo_referido: 'novio', // Solo si tipo_concepto es comision_referido
    valor_con_iva: 0,
    valor_base_sin_iva: 0,
    iva_incluido: 0,
    porcentaje_comision: 5,
    valor_comision: 0,
    valor_pagar: 0, // Valor libre para conceptos sin cálculo
    valor_editado: null,
    motivo_ajuste: '',
    observaciones: ''
  }]);
  
  // Estado de la cuenta
  const [estadoCuenta, setEstadoCuenta] = useState('borrador');
  
  // Vista previa
  const [showVistaPrevia, setShowVistaPrevia] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  // Cargar aliados al montar
  useEffect(() => {
    cargarAliados();
  }, []);

  // Cargar referidos cuando se selecciona aliado (solo para modo referidos)
  useEffect(() => {
    if (aliadoSeleccionado && modoCreacion === 'referidos') {
      cargarReferidosDisponibles(aliadoSeleccionado);
    }
  }, [aliadoSeleccionado, modoCreacion]);

  // Cargar datos del aliado cuando se selecciona (para TODOS los modos)
  useEffect(() => {
    if (aliadoSeleccionado) {
      cargarDatosAliado(aliadoSeleccionado);
      setUsarDatosManual(false); // Usar datos del aliado
    } else if (esFlujoDirec) {
      // Si no hay aliado seleccionado en flujo directo, limpiar datos
      setUsarDatosManual(true);
    }
  }, [aliadoSeleccionado, esFlujoDirec]);

  const cargarAliados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliados(response.data.aliados || []);
    } catch (error) {
      console.error('Error cargando aliados:', error);
    }
  };

  const cargarDatosAliado = async (aliadoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${aliadoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const aliado = response.data;
      setAliadoData(aliado);
      
      // Llenar datos del aliado
      setDatosAliado({
        nombre: aliado.nombre || '',
        tipo_documento: aliado.tipo_documento || 'cc',
        numero_documento: aliado.numero_documento || '',
        telefono: aliado.telefono || '',
        direccion: aliado.direccion || '',
        correo: aliado.correo || '',
        ciudad: aliado.ciudad || 'Bogotá'
      });
      
      // Actualizar DEBE A con el nombre del aliado
      setDatosCobro(prev => ({
        ...prev,
        debe_a: aliado.nombre || ''
      }));
      
      // Llenar datos bancarios
      const dc = aliado.datos_cobro || {};
      setDatosBancarios({
        titular_cuenta: dc.titular_cuenta || aliado.nombre || '',
        tipo_documento_titular: dc.tipo_documento_titular || 'cc',
        documento_titular: dc.documento_titular || aliado.numero_documento || '',
        banco: dc.banco || '',
        tipo_cuenta: dc.tipo_cuenta || 'ahorros',
        numero_cuenta: dc.numero_cuenta || '',
        llave_pago: dc.llave_pago || ''
      });
    } catch (error) {
      console.error('Error cargando datos del aliado:', error);
    }
  };

  const cargarReferidosDisponibles = async (aliadoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${aliadoId}/referidos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo referidos con comisión pendiente
      const disponibles = (response.data.referidos || []).filter(r => 
        r.estado_comision === 'calculada' && 
        !r.id_cuenta_cobro &&
        r.valor_comision > 0
      );
      setReferidosDisponibles(disponibles);
    } catch (error) {
      console.error('Error cargando referidos:', error);
    }
  };

  // Calcular valores de un concepto
  const calcularValoresConcepto = (concepto) => {
    const valorConIva = parseFloat(concepto.valor_con_iva) || 0;
    const valorBaseSinIva = Math.round(valorConIva / 1.19);
    const ivaIncluido = Math.round(valorConIva - valorBaseSinIva);
    const porcentaje = parseFloat(concepto.porcentaje_comision) || 0;
    const valorComision = Math.round(valorBaseSinIva * (porcentaje / 100));
    
    return {
      ...concepto,
      valor_base_sin_iva: valorBaseSinIva,
      iva_incluido: ivaIncluido,
      valor_comision: valorComision
    };
  };

  // Actualizar concepto
  const actualizarConcepto = (index, campo, valor) => {
    const nuevosConceptos = [...conceptosManuales];
    nuevosConceptos[index] = {
      ...nuevosConceptos[index],
      [campo]: valor
    };
    
    // Si cambia el tipo de concepto
    if (campo === 'tipo_concepto') {
      const tipoInfo = TIPOS_CONCEPTO.find(t => t.value === valor);
      if (tipoInfo && !tipoInfo.requiereCalculo) {
        // Limpiar campos de cálculo para conceptos simples
        nuevosConceptos[index].valor_con_iva = 0;
        nuevosConceptos[index].valor_base_sin_iva = 0;
        nuevosConceptos[index].iva_incluido = 0;
        nuevosConceptos[index].porcentaje_comision = 0;
        nuevosConceptos[index].valor_comision = 0;
      }
    }
    
    // Si cambia el tipo de referido (solo para comision_referido)
    if (campo === 'tipo_referido' && nuevosConceptos[index].tipo_concepto === 'comision_referido') {
      const tipoInfo = TIPOS_REFERIDO.find(t => t.value === valor);
      if (tipoInfo) {
        nuevosConceptos[index].porcentaje_comision = tipoInfo.porcentaje;
      }
    }
    
    // Recalcular valores solo si es comisión por referido
    if (nuevosConceptos[index].tipo_concepto === 'comision_referido') {
      if (['valor_con_iva', 'porcentaje_comision'].includes(campo)) {
        nuevosConceptos[index] = calcularValoresConcepto(nuevosConceptos[index]);
      }
    }
    
    setConceptosManuales(nuevosConceptos);
  };

  // Editar valor final manualmente
  const editarValorFinal = (index, nuevoValor, motivo) => {
    const nuevosConceptos = [...conceptosManuales];
    nuevosConceptos[index] = {
      ...nuevosConceptos[index],
      valor_editado: parseFloat(nuevoValor) || 0,
      motivo_ajuste: motivo
    };
    setConceptosManuales(nuevosConceptos);
  };

  // Agregar concepto
  const agregarConcepto = () => {
    setConceptosManuales([...conceptosManuales, {
      id: Date.now(),
      tipo_concepto: 'servicio',
      concepto_cobro: '',
      cliente_tercero: '',
      tipo_referido: 'novio',
      valor_con_iva: 0,
      valor_base_sin_iva: 0,
      iva_incluido: 0,
      porcentaje_comision: 5,
      valor_comision: 0,
      valor_pagar: 0,
      valor_editado: null,
      motivo_ajuste: '',
      observaciones: ''
    }]);
  };

  // Eliminar concepto
  const eliminarConcepto = (index) => {
    if (conceptosManuales.length > 1) {
      setConceptosManuales(conceptosManuales.filter((_, i) => i !== index));
    }
  };

  // Calcular total
  const calcularTotal = () => {
    if (modoCreacion === 'referidos') {
      return referidosSeleccionados.reduce((sum, refId) => {
        const ref = referidosDisponibles.find(r => r.id === refId);
        return sum + (ref?.valor_comision || 0);
      }, 0);
    } else {
      return conceptosManuales.reduce((sum, c) => {
        // Si tiene valor editado, usarlo
        if (c.valor_editado !== null) return sum + c.valor_editado;
        
        // Si es comisión por referido, usar valor_comision calculado
        if (c.tipo_concepto === 'comision_referido') {
          return sum + (c.valor_comision || 0);
        }
        
        // Para otros tipos, usar valor_pagar directo
        return sum + (c.valor_pagar || 0);
      }, 0);
    }
  };

  // Validar formulario
  const validarFormulario = () => {
    if (!tipoBeneficiario) {
      toast.error('Seleccione el tipo de beneficiario');
      return false;
    }
    
    // VALIDACIÓN OBLIGATORIA: Aliado debe estar seleccionado del sistema
    if (!aliadoSeleccionado) {
      toast.error('Debes seleccionar un aliado existente para crear la cuenta de cobro.');
      return false;
    }
    
    if (!datosAliado.nombre) {
      toast.error('Ingrese el nombre del aliado/beneficiario');
      return false;
    }
    
    // Validar nombre completo del titular de la cuenta (nombres y apellidos)
    const nombreTitular = datosBancarios.titular_cuenta?.trim() || '';
    const palabrasNombre = nombreTitular.split(/\s+/).filter(p => p.length > 0);
    
    if (palabrasNombre.length < 2) {
      toast.error('Debe ingresar el nombre completo del titular (nombres y apellidos). Sin esta información, la cuenta NO podrá ser pagada.');
      return false;
    }
    
    if (modoCreacion === 'referidos' && referidosSeleccionados.length === 0) {
      toast.error('Seleccione al menos un referido');
      return false;
    }
    
    if (modoCreacion === 'manual') {
      // Validar que cada concepto tenga concepto_cobro y valor
      const conceptosValidos = conceptosManuales.filter(c => {
        const tieneConcepto = c.concepto_cobro && c.concepto_cobro.trim() !== '';
        const tieneValor = c.tipo_concepto === 'comision_referido' 
          ? (c.valor_comision > 0 || c.valor_editado > 0)
          : (c.valor_pagar > 0 || c.valor_editado > 0);
        return tieneConcepto && tieneValor;
      });
      
      if (conceptosValidos.length === 0) {
        toast.error('Agregue al menos un concepto con descripción y valor');
        return false;
      }
    }
    
    return true;
  };

  // Guardar borrador
  const guardarBorrador = async () => {
    if (!validarFormulario()) return;
    
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const payload = prepararPayload('borrador');
      
      await axios.post(`${API_URL}/api/cuentas-cobro/manual`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Borrador guardado correctamente');
      onSuccess?.();
    } catch (error) {
      let errorMsg = 'Error al guardar borrador';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || String(e)).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error(errorMsg);
    } finally {
      setGuardando(false);
    }
  };

  // Generar vista previa
  const generarVistaPrevia = async () => {
    if (!validarFormulario()) return;
    
    setGenerandoPDF(true);
    try {
      const token = localStorage.getItem('token');
      const payload = prepararPayload('borrador');
      
      const response = await axios.post(
        `${API_URL}/api/cuentas-cobro/manual/preview`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setShowVistaPrevia(true);
    } catch (error) {
      toast.error('Error al generar vista previa');
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Emitir cuenta (o enviar a aprobación si es aliado)
  const emitirCuenta = async () => {
    if (!validarFormulario()) return;
    
    setGenerandoPDF(true);
    try {
      const token = localStorage.getItem('token');
      const esAliado = user?.role === 'aliado';
      const payload = prepararPayload(esAliado ? 'pendiente_aprobacion' : 'emitida');
      
      const response = await axios.post(
        `${API_URL}/api/cuentas-cobro/manual`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: esAliado ? 'json' : 'blob'
        }
      );
      
      // Si es aliado, la respuesta es JSON, no PDF
      if (esAliado) {
        toast.success('Cuenta de cobro enviada para aprobación. Gerencia la revisará.');
        onSuccess?.();
        return;
      }
      
      // Descargar PDF (solo para Admin/Gerencia)
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuenta_cobro_manual_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Cuenta de cobro emitida correctamente');
      onSuccess?.();
    } catch (error) {
      let errorMsg = 'Error al emitir cuenta';
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          // Manejar errores de Pydantic (array de objetos)
          if (Array.isArray(json.detail)) {
            errorMsg = json.detail.map(e => e.msg || String(e)).join(', ');
          } else if (typeof json.detail === 'string') {
            errorMsg = json.detail;
          } else if (json.detail?.msg) {
            errorMsg = json.detail.msg;
          }
        } catch {}
      } else if (error.response?.data?.detail) {
        // Manejar errores de Pydantic directamente
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || String(e)).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error(errorMsg);
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Preparar payload
  const prepararPayload = (estado) => {
    const conceptos = modoCreacion === 'referidos' 
      ? referidosSeleccionados.map(refId => {
          const ref = referidosDisponibles.find(r => r.id === refId);
          return {
            tipo_concepto: 'comision_referido',
            concepto_cobro: `Comisión por referido - ${ref?.tipo_referido || 'Referido'}`,
            cliente_tercero: ref?.nombre || '',
            tipo_referido: ref?.tipo_referido || 'otro',
            valor_con_iva: ref?.valor_cotizacion || 0,
            valor_base_sin_iva: ref?.valor_base_sin_iva || Math.round((ref?.valor_cotizacion || 0) / 1.19),
            iva_incluido: (ref?.valor_cotizacion || 0) - (ref?.valor_base_sin_iva || Math.round((ref?.valor_cotizacion || 0) / 1.19)),
            porcentaje_comision: ref?.porcentaje_comision || 0,
            valor_comision: ref?.valor_comision || 0,
            valor_pagar: ref?.valor_comision || 0,
            valor_editado: null,
            motivo_ajuste: '',
            referido_id: ref?.id
          };
        })
      : conceptosManuales.map(c => {
          const valorFinal = c.valor_editado !== null 
            ? c.valor_editado 
            : (c.tipo_concepto === 'comision_referido' ? c.valor_comision : c.valor_pagar);
          
          return {
            tipo_concepto: c.tipo_concepto,
            concepto_cobro: c.concepto_cobro,
            cliente_tercero: c.cliente_tercero || '',
            tipo_referido: c.tipo_concepto === 'comision_referido' ? c.tipo_referido : null,
            valor_con_iva: c.valor_con_iva || 0,
            valor_base_sin_iva: c.valor_base_sin_iva || 0,
            iva_incluido: c.iva_incluido || 0,
            porcentaje_comision: c.porcentaje_comision || 0,
            valor_comision: c.valor_comision || 0,
            valor_pagar: valorFinal,
            valor_editado: c.valor_editado,
            motivo_ajuste: c.motivo_ajuste || '',
            observaciones: c.observaciones || '',
            referido_id: null
          };
        });
    
    return {
      modo_creacion: modoCreacion,
      tipo_beneficiario: tipoBeneficiario, // NUEVO
      aliado_id: aliadoSeleccionado || null,
      datos_aliado: datosAliado,
      datos_bancarios: datosBancarios,
      datos_cobro: {
        ...datosCobro,
        debe_a: datosCobro.debe_a || datosAliado.nombre // Usar nombre del aliado si no se especifica
      },
      conceptos: conceptos,
      total: calcularTotal(),
      estado: estado,
      referidos_ids: modoCreacion === 'referidos' ? referidosSeleccionados : []
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-5xl mx-4 my-auto">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileText className={`w-5 h-5 ${user?.role === 'aliado' ? 'text-amber-500' : 'text-emerald-500'}`} />
              {user?.role === 'aliado' ? 'Crear Mi Cuenta de Cobro' : 'Crear Cuenta de Cobro Manual'}
            </h2>
            <p className="text-sm text-zinc-400 mt-1">
              {user?.role === 'aliado' 
                ? 'Tu cuenta quedará pendiente de aprobación por Gerencia' 
                : 'Solo disponible para Administración y Gerencia'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* PASO 1: Tipo de beneficiario */}
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <label className="block text-sm font-medium mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              Tipo de Beneficiario <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-zinc-400 mb-4">
              Selecciona el tipo de beneficiario para determinar el flujo de la cuenta de cobro
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {TIPOS_BENEFICIARIO.map(tipo => {
                const IconComponent = tipo.icon;
                const isSelected = tipoBeneficiario === tipo.value;
                return (
                  <button
                    key={tipo.value}
                    type="button"
                    onClick={() => {
                      setTipoBeneficiario(tipo.value);
                      // Resetear datos si cambia el tipo
                      if (tipo.value !== 'aliado_referido') {
                        setAliadoSeleccionado('');
                        setUsarDatosManual(true);
                      } else {
                        setUsarDatosManual(false);
                      }
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                    }`}
                    data-testid={`tipo-beneficiario-${tipo.value}`}
                  >
                    <IconComponent className={`w-5 h-5 mb-2 ${isSelected ? 'text-[#D4AF37]' : 'text-zinc-400'}`} />
                    <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                      {tipo.label}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{tipo.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mostrar contenido solo si se seleccionó tipo de beneficiario */}
          {tipoBeneficiario && (
            <>
              {/* Info según tipo */}
              <div className={`border rounded-lg p-4 ${
                esFlujoDirec 
                  ? 'bg-blue-500/10 border-blue-500/30' 
                  : 'bg-emerald-500/10 border-emerald-500/30'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    esFlujoDirec ? 'text-blue-400' : 'text-emerald-400'
                  }`} />
                  <div>
                    {esFlujoDirec ? (
                      <>
                        <p className="font-medium text-blue-300">Cuenta de Cobro Directa</p>
                        <p className="text-sm text-zinc-300 mt-1">
                          Ingresa los datos del beneficiario y los conceptos a pagar. No se requiere referido, cotización ni cálculo de comisiones.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-emerald-300">Comisiones por Referidos</p>
                        <p className="text-sm text-zinc-300 mt-1">
                          Selecciona un aliado existente y sus referidos pendientes de pago. El sistema calculará automáticamente las comisiones.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Seleccionar aliado - Solo para aliados referidores */}
              {!esFlujoDirec && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Aliado <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={aliadoSeleccionado}
                      onChange={(e) => setAliadoSeleccionado(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                    >
                      <option value="">-- Seleccionar aliado existente --</option>
                      {aliados.map(a => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Estado inicial</label>
                    <select
                      value={estadoCuenta}
                      onChange={(e) => setEstadoCuenta(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                    >
                      <option value="borrador">Borrador</option>
                      <option value="emitida">Emitida</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Para flujo directo: También requiere aliado existente */}
              {esFlujoDirec && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Aliado / Beneficiario <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={aliadoSeleccionado}
                      onChange={(e) => {
                        setAliadoSeleccionado(e.target.value);
                        setUsarDatosManual(!e.target.value);
                      }}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    >
                      <option value="">-- Seleccionar aliado existente --</option>
                      {aliados.map(a => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                    <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      El aliado debe estar registrado previamente en el sistema
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Estado inicial</label>
                    <select
                      value={estadoCuenta}
                      onChange={(e) => setEstadoCuenta(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-blue-500 focus:outline-none"
                    >
                      <option value="borrador">Borrador</option>
                      <option value="emitida">Emitida</option>
                    </select>
                  </div>
                </div>
              )}

          {/* Mensaje si no hay aliado seleccionado */}
          {!aliadoSeleccionado && tipoBeneficiario && (
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-amber-400 font-medium">Debes seleccionar un aliado existente</p>
                <p className="text-sm text-zinc-400">Para crear la cuenta de cobro, primero selecciona un aliado registrado en el sistema.</p>
              </div>
            </div>
          )}

          {/* Datos del beneficiario (solo lectura, cargados del aliado seleccionado) */}
          {aliadoSeleccionado && aliadoData && (
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                {esFlujoDirec ? (
                  <>
                    {tipoBeneficiario === 'proveedor' && <Building2 className="w-4 h-4 text-blue-500" />}
                    {tipoBeneficiario === 'prestador_servicio' && <Briefcase className="w-4 h-4 text-blue-500" />}
                    {tipoBeneficiario === 'tercero' && <User className="w-4 h-4 text-blue-500" />}
                    {tipoBeneficiario === 'otro' && <HandCoins className="w-4 h-4 text-blue-500" />}
                    Datos del {TIPOS_BENEFICIARIO.find(t => t.value === tipoBeneficiario)?.label || 'Beneficiario'}
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4 text-emerald-500" />
                    Datos del Aliado
                  </>
                )}
                <span className="text-xs text-zinc-500 ml-2">(Cargados del sistema)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-zinc-400 mb-1">Nombre o Razón Social *</label>
                  <input
                    type="text"
                    value={datosAliado.nombre}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Tipo Doc.</label>
                  <input
                    type="text"
                    value={datosAliado.tipo_documento?.toUpperCase() || 'CC'}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Número Documento</label>
                  <input
                    type="text"
                    value={datosAliado.numero_documento}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Teléfono</label>
                  <input
                    type="text"
                    value={datosAliado.telefono}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={datosAliado.ciudad}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-zinc-400 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={datosAliado.direccion}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Correo</label>
                  <input
                    type="email"
                    value={datosAliado.correo}
                    readOnly
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-300 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Datos bancarios */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-emerald-500" />
              Datos Bancarios para Pago
            </h3>
            
            {/* Alerta obligatoria para nombre completo */}
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-medium text-sm">Nombre completo obligatorio</p>
                <p className="text-amber-200/70 text-xs mt-1">
                  Debe ingresar <strong>nombres y apellidos completos</strong> del titular de la cuenta. 
                  Sin esta información, la cuenta de cobro <strong>NO podrá ser pagada</strong>.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs text-zinc-400 mb-1">
                  Titular de la Cuenta (Nombres y Apellidos Completos) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={datosBancarios.titular_cuenta}
                  onChange={(e) => setDatosBancarios({...datosBancarios, titular_cuenta: e.target.value})}
                  className={`w-full px-3 py-2 bg-zinc-800 border rounded-lg text-sm focus:outline-none ${
                    datosBancarios.titular_cuenta && datosBancarios.titular_cuenta.trim().split(/\s+/).length < 2
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-zinc-700 focus:border-emerald-500'
                  }`}
                  placeholder="Ej: Juan Carlos Pérez García"
                />
                {datosBancarios.titular_cuenta && datosBancarios.titular_cuenta.trim().split(/\s+/).length < 2 && (
                  <p className="text-red-400 text-xs mt-1">⚠️ Ingrese nombres y apellidos completos (mínimo 2 palabras)</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Documento Titular</label>
                <input
                  type="text"
                  value={datosBancarios.documento_titular}
                  onChange={(e) => setDatosBancarios({...datosBancarios, documento_titular: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Banco</label>
                <select
                  value={datosBancarios.banco}
                  onChange={(e) => setDatosBancarios({...datosBancarios, banco: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Seleccionar banco</option>
                  {BANCOS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Tipo de Cuenta</label>
                <select
                  value={datosBancarios.tipo_cuenta}
                  onChange={(e) => setDatosBancarios({...datosBancarios, tipo_cuenta: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                >
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Número de Cuenta</label>
                <input
                  type="text"
                  value={datosBancarios.numero_cuenta}
                  onChange={(e) => setDatosBancarios({...datosBancarios, numero_cuenta: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-zinc-400 mb-1">Llave de Pago (Nequi/Daviplata)</label>
                <input
                  type="text"
                  value={datosBancarios.llave_pago}
                  onChange={(e) => setDatosBancarios({...datosBancarios, llave_pago: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Número o correo para Nequi/Daviplata"
                />
              </div>
            </div>
          </div>

          {/* Datos del cobro */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-medium mb-4">Datos del Cobro</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">DEBE A (Beneficiario)</label>
                <input
                  type="text"
                  value={datosCobro.debe_a || datosAliado.nombre}
                  onChange={(e) => setDatosCobro({...datosCobro, debe_a: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Nombre del aliado o beneficiario que cobra"
                />
                <p className="text-xs text-zinc-500 mt-1">Este es quien recibe el pago (aliado/proveedor)</p>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Período liquidado</label>
                <input
                  type="text"
                  value={datosCobro.periodo}
                  onChange={(e) => setDatosCobro({...datosCobro, periodo: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Ej: Mayo 2026"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-zinc-400 mb-1">Por concepto de</label>
                <input
                  type="text"
                  value={datosCobro.por_concepto}
                  onChange={(e) => setDatosCobro({...datosCobro, por_concepto: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-zinc-400 mb-1">Observaciones</label>
                <textarea
                  value={datosCobro.observaciones}
                  onChange={(e) => setDatosCobro({...datosCobro, observaciones: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* Referidos disponibles (modo referidos) */}
          {modoCreacion === 'referidos' && aliadoSeleccionado && (
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="font-medium mb-4">
                Referidos Disponibles ({referidosDisponibles.length})
              </h3>
              
              {referidosDisponibles.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-10 h-10 text-zinc-500 mx-auto mb-2" />
                  <p className="text-zinc-400">No hay referidos con comisiones pendientes</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {referidosDisponibles.map(ref => (
                    <label
                      key={ref.id}
                      className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg cursor-pointer hover:bg-zinc-800/50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={referidosSeleccionados.includes(ref.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setReferidosSeleccionados([...referidosSeleccionados, ref.id]);
                            } else {
                              setReferidosSeleccionados(referidosSeleccionados.filter(id => id !== ref.id));
                            }
                          }}
                          className="w-4 h-4 rounded text-emerald-500"
                        />
                        <div>
                          <p className="font-medium">{ref.nombre}</p>
                          <p className="text-xs text-zinc-400">
                            {ref.tipo_referido} • {ref.porcentaje_comision}% • Cotización: {formatCurrency(ref.valor_cotizacion)}
                          </p>
                        </div>
                      </div>
                      <span className="text-emerald-400 font-medium">
                        {formatCurrency(ref.valor_comision)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Conceptos manuales (modo manual) */}
          {modoCreacion === 'manual' && (
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">Conceptos de Cobro</h3>
                <button
                  onClick={agregarConcepto}
                  className="flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300"
                >
                  <Plus className="w-4 h-4" />
                  Agregar concepto
                </button>
              </div>
              
              <div className="space-y-4">
                {conceptosManuales.map((concepto, index) => {
                  const tipoInfo = TIPOS_CONCEPTO.find(t => t.value === concepto.tipo_concepto);
                  const requiereCalculo = tipoInfo?.requiereCalculo || false;
                  
                  return (
                    <div key={concepto.id} className="bg-zinc-900 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-zinc-400">Concepto #{index + 1}</span>
                        {conceptosManuales.length > 1 && (
                          <button
                            onClick={() => eliminarConcepto(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Primera fila: Tipo de concepto y Concepto de cobro */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Tipo de Concepto *</label>
                          <select
                            value={concepto.tipo_concepto}
                            onChange={(e) => actualizarConcepto(index, 'tipo_concepto', e.target.value)}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                          >
                            {TIPOS_CONCEPTO.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-zinc-400 mb-1">Concepto de Cobro *</label>
                          <input
                            type="text"
                            value={concepto.concepto_cobro}
                            onChange={(e) => actualizarConcepto(index, 'concepto_cobro', e.target.value)}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                            placeholder="Ej: Servicio de asesoría comercial, Producto entregado, etc."
                          />
                        </div>
                      </div>
                      
                      {/* Segunda fila: Cliente/tercero (opcional) y Valor */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs text-zinc-400 mb-1">Cliente / Tercero Relacionado (opcional)</label>
                          <input
                            type="text"
                            value={concepto.cliente_tercero}
                            onChange={(e) => actualizarConcepto(index, 'cliente_tercero', e.target.value)}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                            placeholder="Nombre del cliente o N/A si no aplica"
                          />
                        </div>
                        
                        {/* Si NO requiere cálculo, mostrar solo Valor a Pagar */}
                        {!requiereCalculo && (
                          <div>
                            <label className="block text-xs text-zinc-400 mb-1">Valor a Pagar *</label>
                            <input
                              type="number"
                              value={concepto.valor_pagar}
                              onChange={(e) => actualizarConcepto(index, 'valor_pagar', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 bg-zinc-800 border border-emerald-500/50 rounded-lg text-sm focus:border-emerald-500 focus:outline-none text-emerald-400"
                              min="0"
                              placeholder="$ Valor"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Campos adicionales SOLO para Comisión por Referido */}
                      {requiereCalculo && (
                        <>
                          <div className="border-t border-zinc-700 pt-3 mt-2">
                            <p className="text-xs text-amber-400 mb-2">Cálculo de comisión por referido</p>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-zinc-400 mb-1">Tipo de Referido</label>
                              <select
                                value={concepto.tipo_referido}
                                onChange={(e) => actualizarConcepto(index, 'tipo_referido', e.target.value)}
                                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                              >
                                {TIPOS_REFERIDO.map(t => (
                                  <option key={t.value} value={t.value}>
                                    {t.label} ({t.porcentaje}%)
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-zinc-400 mb-1">Valor c/IVA</label>
                              <input
                                type="number"
                                value={concepto.valor_con_iva}
                                onChange={(e) => actualizarConcepto(index, 'valor_con_iva', e.target.value)}
                                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                                min="0"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-zinc-400 mb-1">% Comisión</label>
                              <input
                                type="number"
                                value={concepto.porcentaje_comision}
                                onChange={(e) => actualizarConcepto(index, 'porcentaje_comision', e.target.value)}
                                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-zinc-400 mb-1">Comisión Calculada</label>
                              <input
                                type="text"
                                value={formatCurrency(concepto.valor_comision)}
                                readOnly
                                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-sm text-zinc-400"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-3 text-xs text-zinc-500">
                            <div>Base s/IVA: {formatCurrency(concepto.valor_base_sin_iva)}</div>
                            <div>IVA: {formatCurrency(concepto.iva_incluido)}</div>
                            <div>
                              <label className="block text-xs text-zinc-400 mb-1">Valor Final (editable)</label>
                              <input
                                type="number"
                                value={concepto.valor_editado !== null ? concepto.valor_editado : concepto.valor_comision}
                                onChange={(e) => {
                                  const nuevoValor = parseFloat(e.target.value) || 0;
                                  if (nuevoValor !== concepto.valor_comision) {
                                    const motivo = prompt('Motivo del ajuste manual:');
                                    if (motivo) {
                                      editarValorFinal(index, nuevoValor, motivo);
                                    }
                                  }
                                }}
                                className="w-full px-3 py-2 bg-zinc-800 border border-amber-500/50 rounded-lg text-sm focus:border-emerald-500 focus:outline-none text-amber-400"
                                min="0"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Observaciones */}
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Observaciones (opcional)</label>
                        <input
                          type="text"
                          value={concepto.observaciones}
                          onChange={(e) => actualizarConcepto(index, 'observaciones', e.target.value)}
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                          placeholder="Notas adicionales"
                        />
                      </div>
                      
                      {concepto.valor_editado !== null && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-xs">
                          <span className="text-amber-400">Valor ajustado manualmente.</span>
                          <span className="text-zinc-400 ml-2">Motivo: {concepto.motivo_ajuste}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resumen y Total */}
          <div className={`border rounded-xl p-4 ${
            esFlujoDirec 
              ? 'bg-blue-500/10 border-blue-500/30' 
              : 'bg-emerald-500/10 border-emerald-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Total a pagar</p>
                <p className={`text-3xl font-bold ${esFlujoDirec ? 'text-blue-400' : 'text-emerald-400'}`}>
                  {formatCurrency(calcularTotal())}
                </p>
              </div>
              <div className="text-right text-sm text-zinc-400">
                <p>Tipo: {TIPOS_BENEFICIARIO.find(t => t.value === tipoBeneficiario)?.label || 'N/A'}</p>
                <p>Conceptos: {modoCreacion === 'referidos' ? referidosSeleccionados.length : conceptosManuales.length}</p>
                <p>Creado por: {user?.username || user?.email}</p>
              </div>
            </div>
          </div>
            </> 
          )}
        </div>

        {/* Footer con botones */}
        <div className="p-6 border-t border-zinc-800 flex flex-wrap gap-3 justify-end sticky bottom-0 bg-zinc-900">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
          >
            Cancelar
          </button>
          {/* Guardar borrador solo para Admin/Gerencia */}
          {(user?.role === 'admin' || user?.role === 'gerencia') && (
            <button
              onClick={guardarBorrador}
              disabled={guardando}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg"
            >
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar Borrador
            </button>
          )}
          <button
            onClick={generarVistaPrevia}
            disabled={generandoPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            {generandoPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Vista Previa PDF
          </button>
          <button
            onClick={emitirCuenta}
            disabled={generandoPDF}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              user?.role === 'aliado' 
                ? 'bg-amber-600 hover:bg-amber-700' 
                : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {generandoPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {user?.role === 'aliado' ? 'Enviar para Aprobación' : 'Emitir Cuenta de Cobro'}
          </button>
        </div>
      </div>

      {/* Modal Vista Previa PDF */}
      {showVistaPrevia && pdfPreviewUrl && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl mx-4 h-[90vh] flex flex-col">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-medium">Vista Previa del PDF</h3>
              <button
                onClick={() => {
                  setShowVistaPrevia(false);
                  window.URL.revokeObjectURL(pdfPreviewUrl);
                  setPdfPreviewUrl(null);
                }}
                className="p-2 hover:bg-zinc-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4">
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-full rounded-lg"
                title="Vista previa PDF"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrearCuentaCobroManual;
